import type * as THREE from 'three';

/** River sections by start distance, for "where did it get slow". */
const SECTIONS: readonly [d: number, name: string][] = [
  [0, 'warm-up'],
  [18, 'slalom'],
  [43, 'waterfall 1'],
  [46, 'pool'],
  [56, 'canyon'],
  [88, 'waterfall 2'],
  [99, 'split'],
  [128, 'waterfall 3'],
  [138, 'zig-zag'],
  [174, 'logs'],
  [204, 'rapids'],
  [230, 'sluice'],
  [248, 'pipe'],
  [268, 'herons'],
];

const REPORT_SECONDS = 5;
const LONG_FRAME_MS = 33;

type Window = { intervals: number[]; work: number[]; sections: Map<string, number[]> };

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

function round(n: number, places = 1): number {
  const k = 10 ** places;
  return Math.round(n * k) / k;
}

/**
 * Frame timing for phone testing. Measures real frame-to-frame time (the game
 * loop clamps its delta, which would hide stutters), the JS work per frame,
 * and the renderer's load. With `overlay` it shows a corner readout; with
 * `report` (dev server only) it posts a summary every few seconds to
 * `/__perf`, which the Vite dev server appends to `perf-logs/perf.jsonl`.
 */
export class PerfMonitor {
  private last = 0;
  private window: Window = { intervals: [], work: [], sections: new Map() };
  private sinceReport = 0;
  private sinceOverlay = 0;
  private readonly session = Math.random().toString(36).slice(2, 8);
  private readonly overlayEl: HTMLElement | null = null;
  private readonly gpu: string;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly options: { overlay: boolean; report: boolean },
  ) {
    const gl = renderer.getContext();
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    this.gpu = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER));
    if (options.overlay) {
      const el = document.createElement('div');
      el.id = 'perf-overlay';
      document.body.append(el);
      this.overlayEl = el;
    }
  }

  get active(): boolean {
    return this.options.overlay || this.options.report;
  }

  /** Call once per rendered frame with the JS time spent on it (ms). */
  frame(workMs: number, distance: number, racing: boolean, extra: Record<string, unknown>): void {
    const now = performance.now();
    if (this.last === 0) {
      this.last = now;
      return;
    }
    const interval = now - this.last;
    this.last = now;
    // A backgrounded tab or a pause makes a huge gap: not a stutter.
    if (interval > 1000) return;
    this.window.intervals.push(interval);
    this.window.work.push(workMs);
    if (racing) {
      const section = [...SECTIONS].reverse().find(([d]) => distance >= d)?.[1] ?? 'start';
      const list = this.window.sections.get(section) ?? [];
      list.push(interval);
      this.window.sections.set(section, list);
    }
    this.sinceOverlay += interval;
    this.sinceReport += interval;
    if (this.overlayEl && this.sinceOverlay > 500) {
      this.sinceOverlay = 0;
      this.drawOverlay();
    }
    if (this.sinceReport > REPORT_SECONDS * 1000) {
      this.sinceReport = 0;
      if (this.options.report) this.send(racing, distance, extra);
      this.window = { intervals: [], work: [], sections: new Map() };
    }
  }

  private summary() {
    const sorted = [...this.window.intervals].sort((a, b) => a - b);
    const work = [...this.window.work].sort((a, b) => a - b);
    const avg = sorted.reduce((a, b) => a + b, 0) / Math.max(1, sorted.length);
    return {
      frames: sorted.length,
      fps: round(1000 / Math.max(1, avg)),
      avgMs: round(avg),
      p95Ms: round(percentile(sorted, 0.95)),
      p99Ms: round(percentile(sorted, 0.99)),
      maxMs: round(sorted[sorted.length - 1] ?? 0),
      longFrames: sorted.filter((ms) => ms > LONG_FRAME_MS).length,
      workAvgMs: round(work.reduce((a, b) => a + b, 0) / Math.max(1, work.length)),
      workP95Ms: round(percentile(work, 0.95)),
    };
  }

  private drawOverlay(): void {
    if (!this.overlayEl) return;
    const s = this.summary();
    const info = this.renderer.info.render;
    this.overlayEl.textContent = `${s.fps} fps · ${s.avgMs} ms (p99 ${s.p99Ms}) · js ${s.workAvgMs} ms · ${info.calls} calls · ${round(info.triangles / 1000)}k tris`;
  }

  private send(racing: boolean, distance: number, extra: Record<string, unknown>): void {
    const sections = Object.fromEntries(
      [...this.window.sections].map(([name, list]) => {
        const sorted = [...list].sort((a, b) => a - b);
        const avg = sorted.reduce((a, b) => a + b, 0) / sorted.length;
        return [name, { frames: sorted.length, fps: round(1000 / avg), p95Ms: round(percentile(sorted, 0.95)) }];
      }),
    );
    const canvas = this.renderer.domElement;
    const info = this.renderer.info;
    const report = {
      at: new Date().toISOString(),
      session: this.session,
      ua: navigator.userAgent,
      gpu: this.gpu,
      screen: {
        css: [window.innerWidth, window.innerHeight],
        dpr: window.devicePixelRatio,
        canvas: [canvas.width, canvas.height],
        pixelRatio: this.renderer.getPixelRatio(),
      },
      racing,
      distance: round(distance),
      ...this.summary(),
      sections,
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
        programs: info.programs?.length ?? 0,
      },
      ...extra,
    };
    fetch('/__perf', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(report), keepalive: true }).catch(
      () => {
        // The dev server isn't listening (e.g. a static preview): drop it.
      },
    );
  }

  dispose(): void {
    this.overlayEl?.remove();
  }
}
